/**
 * Chokbox Hypnotic Chocolate Vortex Shader
 * Generates an interactive, concentric WebGL vortex echoing the Chokbox swirl logo.
 * Adapted from Stitch design system.
 */
document.addEventListener('DOMContentLoaded', () => {
  const canvas = document.getElementById('chocolate-vortex-canvas');
  if (!canvas) return;

  function syncSize() {
    const parent = canvas.parentElement;
    const w = (parent ? parent.clientWidth : window.innerWidth) || 1280;
    const h = (parent ? parent.clientHeight : window.innerHeight) || 800;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
  }

  if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
    new ResizeObserver(syncSize).observe(canvas.parentElement);
  } else {
    window.addEventListener('resize', syncSize);
  }
  syncSize();

  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (!gl) {
    console.warn('WebGL not supported — fallback background active');
    return;
  }

  const vs = `
    attribute vec2 a_position;
    varying vec2 v_texCoord;
    void main() {
      v_texCoord = a_position * 0.5 + 0.5;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  const fs = `
    precision highp float;
    uniform float u_time;
    uniform vec2 u_resolution;
    uniform vec2 u_mouse;
    varying vec2 v_texCoord;

    void main() {
      vec2 st = (gl_FragCoord.xy - 0.5 * u_resolution.xy) / min(u_resolution.x, u_resolution.y);
      
      float r = length(st);
      float angle = atan(st.y, st.x);
      
      // Inward vortex spiral motion echoing Chokbox swirl logo
      float swirl = angle + 4.5 * log(r + 0.12) - u_time * 0.5;
      
      // Molten chocolate and liquid caramel ripple frequencies
      float ripple = sin(r * 26.0 - u_time * 1.6 + sin(swirl * 3.0) * 1.2);
      float spiralArms = sin(swirl * 6.0 + sin(r * 10.0 - u_time * 0.8));
      
      // Signature Chokbox Red & Cacao Palette
      vec3 colDeepCacao    = vec3(0.067, 0.02, 0.015);  // deep molten chocolate
      vec3 colDarkRed      = vec3(0.38, 0.04, 0.03);    // deep velvety confectionery red
      vec3 colChokboxRed   = vec3(0.894, 0.196, 0.149); // #e43226 signature Chokbox scarlet
      vec3 colBrightRed    = vec3(1.0, 0.28, 0.20);     // luminous confectionery gloss
      vec3 colGoldFoil     = vec3(0.79, 0.63, 0.30);    // #c9a24c golden ticket metallic foil
      vec3 colGoldShimmer  = vec3(0.96, 0.84, 0.58);    // warm golden glow
      
      // Base mix: radial red spotlight into dark cacao vignette
      float centerVignette = smoothstep(0.05, 0.95, r);
      vec3 color = mix(colChokboxRed, colDarkRed, centerVignette);
      
      // Inward spiral wave layers of molten dark chocolate ribbons
      float fluidMix = smoothstep(-0.5, 0.85, spiralArms * 0.65 + ripple * 0.35);
      color = mix(color, colDeepCacao, fluidMix * (1.0 - smoothstep(0.0, 0.85, r)));
      
      // Concentric hypnotic logo swirl ridges in velvety cacao black
      float spiralRibbon = smoothstep(0.72, 0.96, sin(swirl * 4.0 + ripple * 0.4));
      color = mix(color, vec3(0.08, 0.02, 0.01), spiralRibbon * (1.0 - smoothstep(0.0, 0.9, r)));
      
      // Golden caramel swirl edges and highlights
      float goldRim = smoothstep(0.82, 0.98, sin(swirl * 4.0 - ripple * 0.3));
      float goldAccentMask = exp(-r * 3.2) * 1.5;
      color += colGoldFoil * goldRim * goldAccentMask * 0.85;
      
      // Fine golden sugar dust specks floating within the red vortex
      float noiseDust = sin(st.x * 50.0 + u_time * 0.7) * cos(st.y * 50.0 - u_time * 0.5);
      float dustSpecks = smoothstep(0.94, 0.99, sin(angle * 10.0 + r * 28.0 - u_time * 2.2) * noiseDust);
      color += colGoldShimmer * dustSpecks * (1.0 - smoothstep(0.1, 0.75, r)) * 1.2;
      
      // Epicenter glowing confectionery core
      float coreGlow = exp(-r * 4.2);
      color += mix(colBrightRed, colGoldShimmer, 0.45) * coreGlow * 0.6;
      
      // Outer vignette blending smoothly into dark red/cacao page bounds
      color *= (1.0 - smoothstep(0.7, 1.35, r) * 0.65);

      gl_FragColor = vec4(color, 1.0);
    }
  `;

  function compileShader(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  const vertShader = compileShader(gl.VERTEX_SHADER, vs);
  const fragShader = compileShader(gl.FRAGMENT_SHADER, fs);
  if (!vertShader || !fragShader) return;

  const prog = gl.createProgram();
  gl.attachShader(prog, vertShader);
  gl.attachShader(prog, fragShader);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(prog));
    return;
  }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);

  const pos = gl.getAttribLocation(prog, 'a_position');
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

  const uTime = gl.getUniformLocation(prog, 'u_time');
  const uRes = gl.getUniformLocation(prog, 'u_resolution');
  const uMouse = gl.getUniformLocation(prog, 'u_mouse');

  let mouse = { x: canvas.width / 2, y: canvas.height / 2 };
  window.addEventListener('mousemove', (event) => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width && rect.height) {
      const nx = (event.clientX - rect.left) / rect.width;
      const ny = 1.0 - (event.clientY - rect.top) / rect.height;
      mouse.x = nx * canvas.width;
      mouse.y = ny * canvas.height;
    }
  });

  let running = true;
  function render(t) {
    if (!running) return;
    gl.viewport(0, 0, canvas.width, canvas.height);
    if (uTime) gl.uniform1f(uTime, t * 0.001);
    if (uRes) gl.uniform2f(uRes, canvas.width, canvas.height);
    if (uMouse) gl.uniform2f(uMouse, mouse.x, mouse.y);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    requestAnimationFrame(render);
  }
  requestAnimationFrame(render);

  // Pause when offscreen to save battery
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      running = entry.isIntersecting;
      if (running) requestAnimationFrame(render);
    });
  });
  observer.observe(canvas);
});
